import { createAsyncThunk } from "@reduxjs/toolkit";
import baseAxios from "../../utils/axios";

// Backend errors come as {errors:[{msg}]}, {message} or {error}; surface that text instead of "Request failed with status code 400".
const serverMessage = (e: any): string => {
    const d = e?.response?.data;
    return (typeof d === 'string' ? d : d?.errors?.[0]?.msg || d?.message || d?.error) || e?.message || 'Request failed';
};
const withMessage = (request: Promise<any>) => request.catch((e: any) => { throw new Error(serverMessage(e)); });
// Writes are toasted by the page itself, so the global error toast is silenced to avoid a double toast.
const silent = (config: any = {}) => ({ ...config, headers: { ...(config.headers || {}), 'x-silent': '1' } });
const axios = {
    get: (url: string, config?: any) => withMessage(baseAxios.get(url, config)),
    post: (url: string, data?: any, config?: any) => withMessage(baseAxios.post(url, data, silent(config))),
    put: (url: string, data?: any, config?: any) => withMessage(baseAxios.put(url, data, silent(config))),
    delete: (url: string, config?: any) => withMessage(baseAxios.delete(url, silent(config))),
};

// ===================== LEAVE CONFIG =====================

export const fetchLeaveConfigs = createAsyncThunk(
    'hrms/fetchLeaveConfigs',
    async (accountId: string): Promise<any> => {
        const response = await axios.get(`/api/v1/leaves/config/${accountId}`);
        return response.data;
    }
);

export const createLeaveConfig = createAsyncThunk(
    'hrms/createLeaveConfig',
    async ({ accountId, data }: { accountId: string; data: any }): Promise<any> => {
        const response = await axios.post(`/api/v1/leaves/config/${accountId}`, data);
        return response.data;
    }
);

export const updateLeaveConfig = createAsyncThunk(
    'hrms/updateLeaveConfig',
    async ({ configId, data }: { configId: string; data: any }): Promise<any> => {
        const response = await axios.put(`/api/v1/leaves/config/${configId}`, data);
        return response.data;
    }
);

export const deleteLeaveConfig = createAsyncThunk(
    'hrms/deleteLeaveConfig',
    async (configId: string): Promise<any> => {
        const response = await axios.delete(`/api/v1/leaves/config/${configId}`);
        return response.data;
    }
);

// ===================== SHIFT PATTERN CONFIG =====================

export const fetchShiftConfigs = createAsyncThunk(
    'hrms/fetchShiftConfigs',
    async (): Promise<any> => {
        const response = await axios.get(`/api/v1/shiftPatternConfig`);
        return response.data;
    }
);

export const createShiftConfig = createAsyncThunk(
    'hrms/createShiftConfig',
    async (data: any): Promise<any> => {
        const response = await axios.post(`/api/v1/shiftPatternConfig`, data);
        return response.data;
    }
);

export const updateShiftConfig = createAsyncThunk(
    'hrms/updateShiftConfig',
    async ({ id, data }: { id: string; data: any }): Promise<any> => {
        const response = await axios.put(`/api/v1/shiftPatternConfig/${id}`, data);
        return response.data;
    }
);

export const deleteShiftConfig = createAsyncThunk(
    'hrms/deleteShiftConfig',
    async (id: string): Promise<any> => {
        const response = await axios.delete(`/api/v1/shiftPatternConfig/${id}`);
        return response.data;
    }
);

// ===================== SHIFT PATTERN CONFIG (additional) =====================

export const assignShiftPatternToEmployee = createAsyncThunk(
    'hrms/assignShiftPatternToEmployee',
    async ({ employeeId, configId }: { employeeId: string; configId: string }): Promise<any> => {
        const response = await axios.post(`/api/v1/employee/${employeeId}/shift-pattern-config/${configId}`);
        return response.data;
    }
);

export const assignWeekOffToEmployee = createAsyncThunk(
    'hrms/assignWeekOffToEmployee',
    async ({ employeeId, weekOffId }: { employeeId: string; weekOffId: string }): Promise<any> => {
        const response = await axios.post(`/api/v1/employee/${employeeId}/week-off/${weekOffId}`);
        return response.data;
    }
);

export const removeShiftPatternFromEmployee = createAsyncThunk(
    'hrms/removeShiftPatternFromEmployee',
    async (employeeId: string): Promise<any> => {
        const response = await axios.delete(`/api/v1/employee/${employeeId}/shift-pattern-config`);
        return response.data;
    }
);

export const removeWeekOffFromEmployee = createAsyncThunk(
    'hrms/removeWeekOffFromEmployee',
    async (employeeId: string): Promise<any> => {
        const response = await axios.delete(`/api/v1/employee/${employeeId}/week-off`);
        return response.data;
    }
);

export const fetchEmployeesByAccount = createAsyncThunk(
    'hrms/fetchEmployeesByAccount',
    async ({ accountId, page = 1, limit = 10, filter = '', department = '', designation = '' }: { accountId: string; page?: number; limit?: number; filter?: string; department?: string; designation?: string }): Promise<any> => {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (filter) params.append('filter', filter);
        if (department) params.append('department', department);
        if (designation) params.append('designation', designation);
        const response = await axios.get(`/api/v1/employee/account/${accountId}/emp-list?${params}`);
        return response.data;
    }
);

// Assign shift / shift pattern / week off to many employees; '' removes, undefined keeps.
export const bulkAssignWorkPatterns = createAsyncThunk(
    'hrms/bulkAssignWorkPatterns',
    async ({ accountId, payload }: { accountId: string; payload: any }): Promise<any> => {
        const response = await axios.put(`/api/v1/employee/account/${accountId}/work-patterns/bulk`, payload);
        return response.data;
    }
);

// ===================== HOLIDAYS =====================

export const fetchHolidays = createAsyncThunk(
    'hrms/fetchHolidays',
    async (args?: { year?: number | 'ALL' }): Promise<any> => {
        const params: any = { isActive: true };
        // Year-wise filter: holidays are stored at UTC midnight, so bound the whole calendar year in UTC
        if (args?.year && args.year !== 'ALL') {
            params.dateFrom = `${args.year}-01-01T00:00:00.000Z`;
            params.dateTo = `${args.year}-12-31T23:59:59.999Z`;
        }
        const response = await axios.get(`/api/v1/holidays`, { params });
        return response.data;
    }
);

export const createHoliday = createAsyncThunk(
    'hrms/createHoliday',
    async (data: any): Promise<any> => {
        const response = await axios.post(`/api/v1/holidays`, data);
        return response.data;
    }
);

export const updateHoliday = createAsyncThunk(
    'hrms/updateHoliday',
    async ({ id, data }: { id: string; data: any }): Promise<any> => {
        const response = await axios.put(`/api/v1/holidays/${id}`, data);
        return response.data;
    }
);

export const deleteHoliday = createAsyncThunk(
    'hrms/deleteHoliday',
    async (id: string): Promise<any> => {
        const response = await axios.delete(`/api/v1/holidays/${id}`);
        return response.data;
    }
);

export const importHolidays = createAsyncThunk(
    'hrms/importHolidays',
    async (data: any): Promise<any> => {
        const response = await axios.post(`/api/v1/holidays/import`, data);
        return response.data;
    }
);

// ===================== SHIFTS =====================

export const fetchShifts = createAsyncThunk(
    'hrms/fetchShifts',
    async (): Promise<any> => {
        const response = await axios.get(`/api/v1/worktime/shifts`);
        return response.data;
    }
);

export const createShift = createAsyncThunk(
    'hrms/createShift',
    async (data: any): Promise<any> => {
        const response = await axios.post(`/api/v1/worktime/shifts`, data);
        return response.data;
    }
);

export const updateShift = createAsyncThunk(
    'hrms/updateShift',
    async ({ id, data }: { id: string; data: any }): Promise<any> => {
        const response = await axios.put(`/api/v1/worktime/shifts/${id}`, data);
        return response.data;
    }
);

export const deleteShift = createAsyncThunk(
    'hrms/deleteShift',
    async (id: string): Promise<any> => {
        const response = await axios.delete(`/api/v1/worktime/shifts/${id}`);
        return response.data;
    }
);

// ===================== WORK LOCATIONS =====================

export const fetchWorkLocations = createAsyncThunk(
    'hrms/fetchWorkLocations',
    async (): Promise<any> => {
        const response = await axios.get(`/api/v1/work-locations`);
        return response.data;
    }
);

export const createWorkLocation = createAsyncThunk(
    'hrms/createWorkLocation',
    async (data: any): Promise<any> => {
        const response = await axios.post(`/api/v1/work-locations`, data);
        return response.data;
    }
);

export const updateWorkLocation = createAsyncThunk(
    'hrms/updateWorkLocation',
    async ({ id, data }: { id: string; data: any }): Promise<any> => {
        const response = await axios.put(`/api/v1/work-locations/${id}`, data);
        return response.data;
    }
);

export const deleteWorkLocation = createAsyncThunk(
    'hrms/deleteWorkLocation',
    async (id: string): Promise<any> => {
        const response = await axios.delete(`/api/v1/work-locations/${id}`);
        return response.data;
    }
);

// ===================== WEEK OFFS =====================

export const fetchWeekOffs = createAsyncThunk(
    'hrms/fetchWeekOffs',
    async (): Promise<any> => {
        const response = await axios.get(`/api/v1/worktime/week-offs`);
        return response.data;
    }
);

export const createWeekOff = createAsyncThunk(
    'hrms/createWeekOff',
    async (data: any): Promise<any> => {
        const response = await axios.post(`/api/v1/worktime/week-offs`, data);
        return response.data;
    }
);

export const updateWeekOff = createAsyncThunk(
    'hrms/updateWeekOff',
    async ({ id, data }: { id: string; data: any }): Promise<any> => {
        const response = await axios.put(`/api/v1/worktime/week-offs/${id}`, data);
        return response.data;
    }
);

export const deleteWeekOff = createAsyncThunk(
    'hrms/deleteWeekOff',
    async (id: string): Promise<any> => {
        const response = await axios.delete(`/api/v1/worktime/week-offs/${id}`);
        return response.data;
    }
);
